import React, { useState, useEffect } from "react";
import { MessageSquare, Plus, Heart, MessageCircle, Send, Trash2, Pencil, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

interface Comment {
  id: string;
  author_name: string;
  content: string;
  created_at: string;
  user_id: string;
}

interface Post {
  id: string;
  author_name: string;
  content: string;
  created_at: string;
  user_id: string;
}

export default function General() {
  const { user } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [authorName, setAuthorName] = useState("");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  // Likes state
  const [likesCount, setLikesCount] = useState<Record<string, number>>({});
  const [userLikes, setUserLikes] = useState<Set<string>>(new Set());
  
  // Comments state
  const [commentsCount, setCommentsCount] = useState<Record<string, number>>({});
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [comments, setComments] = useState<Record<string, Comment[]>>({});
  const [newComment, setNewComment] = useState<Record<string, string>>({});
  const [commentAuthor, setCommentAuthor] = useState("");

  // Edit state
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editPostContent, setEditPostContent] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentContent, setEditCommentContent] = useState("");

  useEffect(() => {
    fetchPosts();
  }, [user]);

  // Real-time subscriptions
  useEffect(() => {
    const postsChannel = supabase
      .channel('posts-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'community_posts' },
        (payload) => {
          const newPost = payload.new as Post;
          setPosts(prev => [newPost, ...prev]);
          setLikesCount(prev => ({ ...prev, [newPost.id]: 0 }));
          setCommentsCount(prev => ({ ...prev, [newPost.id]: 0 }));
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'community_posts' },
        (payload) => {
          const deletedPost = payload.old as Post;
          setPosts(prev => prev.filter(p => p.id !== deletedPost.id));
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'community_posts' },
        (payload) => {
          const updatedPost = payload.new as Post;
          setPosts(prev => prev.map(p => p.id === updatedPost.id ? updatedPost : p));
        }
      )
      .subscribe();

    const likesChannel = supabase
      .channel('likes-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'post_likes' },
        (payload) => {
          const newLike = payload.new as { post_id: string; user_id: string };
          setLikesCount(prev => ({ ...prev, [newLike.post_id]: (prev[newLike.post_id] || 0) + 1 }));
          if (user && newLike.user_id === user.id) {
            setUserLikes(prev => new Set([...prev, newLike.post_id]));
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'post_likes' },
        (payload) => {
          const deletedLike = payload.old as { post_id: string; user_id: string };
          setLikesCount(prev => ({ ...prev, [deletedLike.post_id]: Math.max((prev[deletedLike.post_id] || 1) - 1, 0) }));
          if (user && deletedLike.user_id === user.id) {
            setUserLikes(prev => {
              const newSet = new Set(prev);
              newSet.delete(deletedLike.post_id);
              return newSet;
            });
          }
        }
      )
      .subscribe();

    const commentsChannel = supabase
      .channel('comments-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'post_comments' },
        (payload) => {
          const newComment = payload.new as Comment & { post_id: string };
          setCommentsCount(prev => ({ ...prev, [newComment.post_id]: (prev[newComment.post_id] || 0) + 1 }));
          setComments(prev => {
            if (prev[newComment.post_id]) {
              return { ...prev, [newComment.post_id]: [...prev[newComment.post_id], newComment] };
            }
            return prev;
          });
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'post_comments' },
        (payload) => {
          const deletedComment = payload.old as Comment & { post_id: string };
          setCommentsCount(prev => ({ ...prev, [deletedComment.post_id]: Math.max((prev[deletedComment.post_id] || 1) - 1, 0) }));
          setComments(prev => {
            if (prev[deletedComment.post_id]) {
              return { ...prev, [deletedComment.post_id]: prev[deletedComment.post_id].filter(c => c.id !== deletedComment.id) };
            }
            return prev;
          });
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'post_comments' },
        (payload) => {
          const updatedComment = payload.new as Comment & { post_id: string };
          setComments(prev => {
            if (prev[updatedComment.post_id]) {
              return { ...prev, [updatedComment.post_id]: prev[updatedComment.post_id].map(c => c.id === updatedComment.id ? updatedComment : c) };
            }
            return prev;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(postsChannel);
      supabase.removeChannel(likesChannel);
      supabase.removeChannel(commentsChannel);
    };
  }, [user]);

  const fetchPosts = async () => {
    const { data, error } = await supabase
      .from("community_posts")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching posts:", error);
    } else {
      setPosts(data || []);
      if (data && data.length > 0) {
        await fetchLikesAndComments(data.map(p => p.id));
      }
    }
    setLoading(false);
  };

  const fetchLikesAndComments = async (postIds: string[]) => {
    const { data: likesData } = await supabase
      .from("post_likes")
      .select("post_id")
      .in("post_id", postIds);

    const likeCounts: Record<string, number> = {};
    postIds.forEach(id => likeCounts[id] = 0);
    likesData?.forEach(like => {
      likeCounts[like.post_id] = (likeCounts[like.post_id] || 0) + 1;
    });
    setLikesCount(likeCounts);

    if (user) {
      const { data: userLikesData } = await supabase
        .from("post_likes")
        .select("post_id")
        .eq("user_id", user.id)
        .in("post_id", postIds);

      setUserLikes(new Set(userLikesData?.map(l => l.post_id) || []));
    }

    const { data: commentsData } = await supabase
      .from("post_comments")
      .select("post_id")
      .in("post_id", postIds);

    const commentCounts: Record<string, number> = {};
    postIds.forEach(id => commentCounts[id] = 0);
    commentsData?.forEach(comment => {
      commentCounts[comment.post_id] = (commentCounts[comment.post_id] || 0) + 1;
    });
    setCommentsCount(commentCounts);
  };

  const handleLike = async (postId: string) => {
    if (!user) {
      toast.error("Please log in to like posts");
      return;
    }

    const isLiked = userLikes.has(postId);

    if (isLiked) {
      const { error } = await supabase
        .from("post_likes")
        .delete()
        .eq("post_id", postId)
        .eq("user_id", user.id);

      if (!error) {
        setUserLikes(prev => {
          const newSet = new Set(prev);
          newSet.delete(postId);
          return newSet;
        });
        setLikesCount(prev => ({ ...prev, [postId]: (prev[postId] || 1) - 1 }));
      }
    } else {
      const { error } = await supabase
        .from("post_likes")
        .insert({ post_id: postId, user_id: user.id });

      if (!error) {
        setUserLikes(prev => new Set([...prev, postId]));
        setLikesCount(prev => ({ ...prev, [postId]: (prev[postId] || 0) + 1 }));
      }
    }
  };

  const toggleComments = async (postId: string) => {
    const isExpanded = expandedComments.has(postId);
    
    if (isExpanded) {
      setExpandedComments(prev => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
    } else {
      const { data } = await supabase
        .from("post_comments")
        .select("*")
        .eq("post_id", postId)
        .order("created_at", { ascending: true });

      setComments(prev => ({ ...prev, [postId]: data || [] }));
      setExpandedComments(prev => new Set([...prev, postId]));
    }
  };

  const handleAddComment = async (postId: string) => {
    if (!user) {
      toast.error("Please log in to comment");
      return;
    }

    const commentContent = newComment[postId]?.trim();
    if (!commentContent || !commentAuthor.trim()) {
      toast.error("Please enter your name and comment");
      return;
    }

    const { data, error } = await supabase
      .from("post_comments")
      .insert({
        post_id: postId,
        user_id: user.id,
        author_name: commentAuthor.trim(),
        content: commentContent,
      })
      .select()
      .single();

    if (error) {
      toast.error("Failed to add comment");
    } else {
      setComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), data],
      }));
      setCommentsCount(prev => ({ ...prev, [postId]: (prev[postId] || 0) + 1 }));
      setNewComment(prev => ({ ...prev, [postId]: "" }));
      toast.success("Comment added!");
    }
  };

  const handleDeletePost = async (postId: string) => {
    const { error } = await supabase
      .from("community_posts")
      .delete()
      .eq("id", postId);

    if (error) {
      toast.error("Failed to delete post");
    } else {
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success("Post deleted");
    }
  };

  const handleEditPost = (post: Post) => {
    setEditingPostId(post.id);
    setEditPostContent(post.content);
  };

  const handleSaveEditPost = async (postId: string) => {
    if (!editPostContent.trim()) {
      toast.error("Post content cannot be empty");
      return;
    }

    const { error } = await supabase
      .from("community_posts")
      .update({ content: editPostContent.trim() })
      .eq("id", postId);

    if (error) {
      toast.error("Failed to update post");
    } else {
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, content: editPostContent.trim() } : p));
      setEditingPostId(null);
      setEditPostContent("");
      toast.success("Post updated");
    }
  };

  const handleDeleteComment = async (postId: string, commentId: string) => {
    const { error } = await supabase
      .from("post_comments")
      .delete()
      .eq("id", commentId);

    if (error) {
      toast.error("Failed to delete comment");
    } else {
      setComments(prev => ({
        ...prev,
        [postId]: prev[postId]?.filter(c => c.id !== commentId) || [],
      }));
      setCommentsCount(prev => ({ ...prev, [postId]: Math.max((prev[postId] || 1) - 1, 0) }));
      toast.success("Comment deleted");
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditCommentContent(comment.content);
  };

  const handleSaveEditComment = async (postId: string, commentId: string) => {
    if (!editCommentContent.trim()) {
      toast.error("Comment cannot be empty");
      return;
    }

    const { error } = await supabase
      .from("post_comments")
      .update({ content: editCommentContent.trim() })
      .eq("id", commentId);

    if (error) {
      toast.error("Failed to update comment");
    } else {
      setComments(prev => ({
        ...prev,
        [postId]: prev[postId]?.map(c => c.id === commentId ? { ...c, content: editCommentContent.trim() } : c) || [],
      }));
      setEditingCommentId(null);
      setEditCommentContent("");
      toast.success("Comment updated");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!authorName.trim() || !content.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.from("community_posts").insert({
      user_id: user.id,
      author_name: authorName.trim(),
      content: content.trim(),
    });

    if (error) {
      console.error("Error creating post:", error);
      toast.error("Failed to create post");
    } else {
      toast.success("Post created!");
      setAuthorName("");
      setContent("");
      setIsOpen(false);
      fetchPosts();
    }
    setSubmitting(false);
  };

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold">General</h1>
            <p className="text-muted-foreground">Community discussions</p>
          </div>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button variant="secondary">
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create a New Post</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="authorName">Your Name</Label>
                <Input
                  id="authorName"
                  placeholder="Enter your name"
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  maxLength={50}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">What's on your mind?</Label>
                <Textarea
                  id="content"
                  placeholder="Share your thoughts, questions, or wins..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={4}
                  maxLength={1000}
                />
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Posting..." : "Post"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Posts Feed */}
      <div className="space-y-4 max-w-3xl">
        {loading ? (
          <div className="glass-card p-6 text-center text-muted-foreground">
            Loading posts...
          </div>
        ) : posts.length === 0 ? (
          <div className="space-y-4">
            {/* Starter post shown when no posts exist */}
            <div className="glass-card p-6 hover:border-primary/20 transition-colors">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-foreground">The AI StoryCraft Studio Team</p>
                  <p className="text-xs text-muted-foreground">Pinned post</p>
                </div>
              </div>
              <h3 className="font-display text-lg font-semibold mb-2">Introduce yourself — where are you in your creator journey?</h3>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{"Welcome to the community! Drop a comment below and tell us:\n\n- What platform are you creating content on?\n- How many followers do you have right now?\n- What's your #1 goal for the next 30 days?\n\nLet's support each other. 👇"}</p>
            </div>
          </div>
        ) : (
          posts.map((post) => (
            <div key={post.id} className="glass-card p-6">
              {/* Author */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground font-bold">
                    {post.author_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{post.author_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
                {user?.id === post.user_id && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEditPost(post)}
                      className="text-muted-foreground hover:text-foreground transition-colors p-2"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="text-muted-foreground hover:text-destructive transition-colors p-2">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Post</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this post? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeletePost(post.id)}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>

              {/* Content */}
              {editingPostId === post.id ? (
                <div className="mb-4 space-y-2">
                  <Textarea
                    value={editPostContent}
                    onChange={(e) => setEditPostContent(e.target.value)}
                    rows={4}
                    maxLength={1000}
                    className="text-foreground"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleSaveEditPost(post.id)}>
                      <Check className="w-4 h-4 mr-1" />
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditingPostId(null); setEditPostContent(""); }}>
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-foreground mb-4 leading-relaxed whitespace-pre-wrap">{post.content}</p>
              )}

              {/* Actions */}
              <div className="flex items-center gap-6">
                <button 
                  onClick={() => handleLike(post.id)}
                  className={`flex items-center gap-2 transition-colors ${
                    userLikes.has(post.id) 
                      ? "text-red-500" 
                      : "text-muted-foreground hover:text-red-500"
                  }`}
                >
                  <Heart className={`w-5 h-5 ${userLikes.has(post.id) ? "fill-current" : ""}`} />
                  <span className="text-sm">{likesCount[post.id] || 0}</span>
                </button>
                <button 
                  onClick={() => toggleComments(post.id)}
                  className={`flex items-center gap-2 transition-colors ${
                    expandedComments.has(post.id)
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <MessageCircle className="w-5 h-5" />
                  <span className="text-sm">{commentsCount[post.id] || 0}</span>
                </button>
              </div>

              {/* Comments Section */}
              {expandedComments.has(post.id) && (
                <div className="mt-4 pt-4 border-t border-border">
                  {/* Existing Comments */}
                  <div className="space-y-3 mb-4">
                    {(comments[post.id] || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground">No comments yet. Be the first!</p>
                    ) : (
                      (comments[post.id] || []).map((comment) => (
                        <div key={comment.id} className="flex gap-3 group">
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
                            {comment.author_name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{comment.author_name}</span>
                              <span className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                              </span>
                              {user?.id === comment.user_id && (
                                <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 ml-auto transition-all">
                                  <button
                                    onClick={() => handleEditComment(comment)}
                                    className="text-muted-foreground hover:text-foreground"
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteComment(post.id, comment.id)}
                                    className="text-muted-foreground hover:text-destructive"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                            {editingCommentId === comment.id ? (
                              <div className="mt-1 space-y-2">
                                <Input
                                  value={editCommentContent}
                                  onChange={(e) => setEditCommentContent(e.target.value)}
                                  maxLength={500}
                                  className="text-sm"
                                />
                                <div className="flex gap-2">
                                  <Button size="sm" className="h-7 text-xs" onClick={() => handleSaveEditComment(post.id, comment.id)}>
                                    <Check className="w-3 h-3 mr-1" />
                                    Save
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditingCommentId(null); setEditCommentContent(""); }}>
                                    <X className="w-3 h-3 mr-1" />
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-foreground">{comment.content}</p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add Comment */}
                  <div className="space-y-2">
                    {!commentAuthor && (
                      <Input
                        placeholder="Your name"
                        value={commentAuthor}
                        onChange={(e) => setCommentAuthor(e.target.value)}
                        className="text-sm"
                        maxLength={50}
                      />
                    )}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Write a comment..."
                        value={newComment[post.id] || ""}
                        onChange={(e) => setNewComment(prev => ({ ...prev, [post.id]: e.target.value }))}
                        className="flex-1 text-sm"
                        maxLength={500}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleAddComment(post.id);
                          }
                        }}
                      />
                      <Button 
                        size="icon" 
                        onClick={() => handleAddComment(post.id)}
                        disabled={!newComment[post.id]?.trim()}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
